import { useState, useEffect } from "react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Download } from "lucide-react";

const FIELDS = [
  ["campaign_start", "Competition begins", "date"],
  ["campaign_end", "Submissions & reels due", "date"],
  ["judging_start", "Judging starts", "date"],
  ["judging_end", "Judging ends", "date"],
  ["task_weight", "Task weight (%)", "number"],
  ["reel_weight", "Reel weight (%)", "number"],
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [published, setPublished] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
    api.get("/leaderboard").then(({ data }) => setPublished(data.published_at));
  }, []);

  if (!s) return null;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        ...s, task_weight: parseInt(s.task_weight, 10), reel_weight: parseInt(s.reel_weight, 10),
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leaderboard/publish");
      setPublished(data.published_at);
      toast.success("Leaderboard recalculated and published");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/standings", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-wars-standings-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Standings CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 max-w-4xl">
      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-display font-semibold text-[#1a1f36] mb-4">Competition settings</h3>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([key, label, type]) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input type={type} value={s[key]} onChange={(e) => setS({ ...s, [key]: e.target.value })} data-testid={`setting-${key}`} />
            </div>
          ))}
        </div>
        <Button onClick={save} className="mt-4 sw-red hover:bg-red-700 text-white" data-testid="settings-save-btn">Save settings</Button>
      </div>
      <div className="border border-slate-200 rounded-md p-5 h-fit">
        <h3 className="font-display font-semibold text-[#1a1f36] mb-2">Leaderboard control</h3>
        <p className="text-sm text-slate-600 mb-3">Recalculate every score and publish the Friday update to the public leaderboard.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={publish} disabled={busy} className="bg-[#1a1f36] hover:bg-[#2a3152] text-white" data-testid="publish-leaderboard-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Recalculate & publish
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-standings-btn">
            <Download className="w-4 h-4 mr-2" /> Export standings (CSV)
          </Button>
        </div>
        {published && <p className="text-xs text-slate-500 mt-3" data-testid="publish-stamp">Last published: {new Date(published).toLocaleString()}</p>}
        <p className="text-xs text-slate-400 mt-1">The CSV exports exactly what's on the published leaderboard — ready for the Friday email to Regionals.</p>
      </div>
    </div>
  );
}
